import apiClient from '../utils/apiClient';

/**
 * Shift Template API Service
 * Quản lý cấu hình ca làm việc theo chi nhánh
 */
class ShiftTemplateAPI {
  /**
   * Lấy danh sách cấu hình ca
   * @param {Object} params - Tham số tìm kiếm
   * @param {string} params.branch_id - ID chi nhánh
   * @param {boolean} params.is_active - Trạng thái hoạt động
   * @param {number} params.page - Trang
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise} Danh sách cấu hình ca
   */
  getShiftTemplates(params = {}) {
    return apiClient.get({ url: '/shift-templates', params });
  }

  /**
   * Lấy chi tiết cấu hình ca
   * @param {string} id - ID cấu hình ca
   * @returns {Promise} Chi tiết cấu hình ca
   */
  getShiftTemplate(id) {
    return apiClient.get({ url: `/shift-templates/${id}` });
  }

  /**
   * Tạo cấu hình ca mới
   * @param {Object} data - Dữ liệu cấu hình ca
   * @param {string} data.branch_id - ID chi nhánh
   * @param {string} data.name - Tên cấu hình ca
   * @param {number} data.start_hour - Giờ bắt đầu (0-23)
   * @param {number} data.start_minute - Phút bắt đầu (0-59)
   * @param {number} data.duration_minutes - Thời lượng (phút)
   * @param {number} data.max_employees - Số nhân viên tối đa
   * @param {number[]} data.days_of_week - Các ngày trong tuần (0-6)
   * @returns {Promise} Cấu hình ca đã tạo
   */
  createShiftTemplate(data) {
    return apiClient.post({ url: '/shift-templates', data });
  }

  /**
   * Cập nhật cấu hình ca
   * @param {string} id - ID cấu hình ca
   * @param {Object} data - Dữ liệu cập nhật
   * @returns {Promise} Cấu hình ca đã cập nhật
   */
  updateShiftTemplate(id, data) {
    return apiClient.patch({ url: `/shift-templates/${id}`, data });
  }

  /**
   * Xóa cấu hình ca
   * @param {string} id - ID cấu hình ca
   * @returns {Promise} Kết quả xóa
   */
  deleteShiftTemplate(id) {
    return apiClient.delete({ url: `/shift-templates/${id}` });
  }

  /**
   * Gán danh sách task cho template (ghi đè)
   * @param {string} id - ID cấu hình ca
   * @param {Array<{task_id: string, order_no: number}>} tasks - Danh sách task với thứ tự
   * @returns {Promise}
   */
  setTemplateTasks(id, tasks = []) {
    return apiClient.post({ url: `/shift-templates/${id}/tasks`, data: { tasks } });
  }

  // ─── Liên kết Task ↔ Ca: thao tác LẺ (#110145) ──────────────────
  // Dùng cho màn Danh sách task (chiều task → ca). KHÔNG dùng setTemplateTasks()
  // ở đây: nó ghi đè CẢ danh sách task của ca, 2 người sửa cùng lúc sẽ xoá mất
  // task của nhau.

  /**
   * Lấy các ca đang gắn cho nhiều task trong MỘT lượt (chống N+1)
   * @param {Object} params
   * @param {string[]|string} params.task_ids - danh sách id task
   * @param {string} [params.branch_id] - chỉ lấy ca thuộc chi nhánh này
   * @returns {Promise<{data: Object<string, Array>}>} map task_id → mảng liên kết
   */
  getTaskShiftLinks({ task_ids, branch_id } = {}) {
    const ids = Array.isArray(task_ids) ? task_ids.join(',') : task_ids;
    return apiClient.get({
      url: '/shift-templates/task-links',
      params: { task_ids: ids, ...(branch_id ? { branch_id } : {}) },
    });
  }

  /**
   * Thêm ĐÚNG MỘT liên kết task ↔ ca
   * @param {{task_id: string, shift_template_id: string, order_no?: number}} data
   */
  addTaskShiftLink(data) {
    return apiClient.post({ url: '/shift-templates/task-links', data });
  }

  /**
   * Sửa MỘT liên kết (đổi ca và/hoặc đổi thứ tự)
   * @param {string} linkId
   * @param {{shift_template_id?: string, order_no?: number}} data
   */
  updateTaskShiftLink(linkId, data) {
    return apiClient.patch({ url: `/shift-templates/task-links/${linkId}`, data });
  }

  /** Xoá MỘT liên kết, không đụng các liên kết khác */
  deleteTaskShiftLink(linkId) {
    return apiClient.delete({ url: `/shift-templates/task-links/${linkId}` });
  }

  // ─── Bảng ca xếp doanh thu + cờ "ca đông" thủ công (#130536) ─────
  // "Ca đông" KHÔNG còn tự tính top 30% doanh thu; quản lý tự bật/tắt cờ trên bảng này.

  /**
   * Bảng ca xếp doanh thu cao → thấp, kèm chi tiết ca và dự đoán thời gian việc phải nghiệm thu
   * @param {Object} params
   * @param {string} [params.branch_id]
   * @param {number} [params.lookback_weeks] - số tuần lịch sử dùng để tính doanh thu
   * @param {string} [params.until] - mốc kết thúc kỳ tham chiếu (ISO)
   * @param {number} [params.light_max_minutes] - ngưỡng "nhẹ" (phút việc phải nghiệm thu)
   * @param {number} [params.medium_max_minutes] - ngưỡng "vừa" (phút việc phải nghiệm thu)
   */
  getRevenueRanking(params = {}) {
    return apiClient.get({ url: '/shift-templates/revenue-ranking', params });
  }

  /**
   * Bật/tắt cờ "ca đông" cho CẢ ca mẫu (mọi thứ cùng lúc) — nút "bật cả tuần".
   * @param {string} id
   * @param {boolean} isHighRevenue
   */
  setHighRevenue(id, isHighRevenue) {
    return apiClient.patch({
      url: `/shift-templates/${id}/high-revenue`,
      data: { is_high_revenue: isHighRevenue },
    });
  }

  /**
   * #220322 — bật/tắt cờ "ca đông" cho RIÊNG MỘT THỨ của ca mẫu.
   * Bật ở Thứ Bảy KHÔNG đụng tới các thứ khác của cùng ca.
   * @param {string} id ca mẫu
   * @param {number} dayOfWeek 0 = Chủ nhật … 6 = Thứ Bảy
   * @param {boolean} isHighRevenue
   */
  setHighRevenueForDay(id, dayOfWeek, isHighRevenue) {
    return apiClient.patch({
      url: `/shift-templates/${id}/high-revenue-day`,
      data: { day_of_week: dayOfWeek, is_high_revenue: isHighRevenue },
    });
  }
}

export const shiftTemplateAPI = new ShiftTemplateAPI();
export default shiftTemplateAPI;
